import React, { useState, useEffect } from "react"
import { makeStyles } from 'tss-react/mui';
import {
    Select,
    MenuItem,
    Button,
} from "@mui/material"
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined"
import AdminDialog, { DialogSwitchRow } from "./adminDialog"
import { setError } from "../store/features/error/errorSlice"
import { connect } from "react-redux"
import {
    updateTimeFrameDay,
} from "../utils/utils"
import { areObjectsEqual } from "../utils/common"

const mapDispatchToProps = {
    setError,
};

const useStyles = makeStyles()((theme) => ({
    // "Start" or "End" with the hour and the minute.
    time: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    timeLabel: {
        flex: 1,
        fontSize: 14.5,
        color: theme.palette.text.primary,
    },
    timeDisabled: {
        color: theme.palette.text.disabled,
    },
    timeSelect: {
        width: 84,
        fontVariantNumeric: "tabular-nums",
    },
    colon: {
        color: theme.palette.text.secondary,
    },
    textButton: {
        textTransform: "none",
        color: theme.palette.text.secondary,
    },
    reset: {
        marginRight: "auto",
    },
    primary: {
        textTransform: "none",
        minWidth: 112,
        color: "#fff",
        backgroundColor: theme.palette.primary.light,
        boxShadow: "none",
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
            filter: "brightness(0.93)",
            boxShadow: "none",
        },
    },
}));

const TimeFrameDay = (props) => {
    const { classes, cx } = useStyles();
    const {
        open,
        setOpen,
        timeFrameDay,
        refetchNotifications,
        session,
        setError,
    } = props;

    // let open = true
    // let timeFrameDay = props;
    // timeFrameDay = {
    //     day: "Monday",
    //     active: true,
    //     allDay: false,
    //     startTime: "09:00:00",
    //     endTime: "14:00:00",
    // }
    const [currentTimeFrameDay, setCurrentTimeFrameDay] = useState(timeFrameDay)
    const [initTimeFrameDay, setInitTimeFrameDay] = useState(timeFrameDay)
    const [submitting, setSubmitting] = useState(false)

    const handleStartTimeChange = (value, index) => {
        const updatedTime = currentTimeFrameDay.start.split(":")
        updatedTime[index] = value
        handlePropertyChange("start", updatedTime.join(":"))
    };

    const handleEndTimeChange = (value, index) => {
        const updatedTime = currentTimeFrameDay.end.split(":")
        updatedTime[index] = value
        handlePropertyChange("end", updatedTime.join(":"))
    };

    const clearState = () => {
        setOpen(false)
        setCurrentTimeFrameDay(null)
        setInitTimeFrameDay(null)
    }

    useEffect(() => {
        setCurrentTimeFrameDay(timeFrameDay)
        setInitTimeFrameDay(timeFrameDay)
    }, [timeFrameDay]);

    const handlePropertyChange = (field, value) => {
        setCurrentTimeFrameDay({
            ...currentTimeFrameDay,
            [field]: value,
        });
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            let data = {
                day: currentTimeFrameDay.day,
                timeFrameDayDetails: {
                    status: (currentTimeFrameDay.active) ? "active" : "inactive",
                    allDayMode: (currentTimeFrameDay.allDay) ? "yes" : "no",
                    startTime: currentTimeFrameDay.start,
                    endTime: currentTimeFrameDay.end,
                },
                token: session.token,
                server: session.lastServer,
            }
            await updateTimeFrameDay(data);
            setSubmitting(false);

            try {
                refetchNotifications()
                setOpen(false)
                setError({
                    visible: true,
                    type: "success",
                    message: "Day updated."
                });
            } catch (error) {
                setSubmitting(false);
                setError({
                    visible: true,
                    type: "error",
                    message: "Failed to refetch Time Frame. " + error,
                })
                setOpen(false)
            }
        } catch (error) {
            setSubmitting(false);
            setError({
                visible: true,
                type: "error",
                message: "Failed to update Day. " + error,
            })
        }
    };

    const handleReset = () => {
        setCurrentTimeFrameDay(initTimeFrameDay)
    };

    if (!open || !currentTimeFrameDay)
        return null;

    const isModified = !areObjectsEqual(currentTimeFrameDay, initTimeFrameDay)
    const hours = [...Array(24).keys()];
    const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

    const timesDisabled = currentTimeFrameDay.allDay || !currentTimeFrameDay.active;

    const renderTime = (label, value, onChange) => (
        <div className={classes.time}>
            <div className={cx(classes.timeLabel, timesDisabled && classes.timeDisabled)}>{label}</div>
            <Select
                value={value.split(":")[0]}
                onChange={(e) => onChange(e.target.value, 0)}
                disabled={timesDisabled}
                size="small"
                className={classes.timeSelect}
                slotProps={{ input: { "aria-label": `${label} hour` } }}
                MenuProps={{ slotProps: { paper: { style: { maxHeight: 280 } } } }}
            >
                {hours.map((hour) => (
                    <MenuItem key={hour} value={String(hour).padStart(2, "0")}>
                        {String(hour).padStart(2, "0")}
                    </MenuItem>
                ))}
            </Select>
            <span className={classes.colon}>:</span>
            <Select
                value={value.split(":")[1]}
                onChange={(e) => onChange(e.target.value, 1)}
                disabled={timesDisabled}
                size="small"
                className={classes.timeSelect}
                slotProps={{ input: { "aria-label": `${label} minute` } }}
            >
                {minutes.map((minute) => (
                    <MenuItem key={minute} value={String(minute).padStart(2, "0")}>
                        {String(minute).padStart(2, "0")}
                    </MenuItem>
                ))}
            </Select>
        </div>
    );

    return (
        <AdminDialog
            open={open}
            onClose={clearState}
            icon={<ScheduleIcon />}
            title={currentTimeFrameDay.day}
            subtitle="Time frame for this day"
            footer={
                <>
                    <Button
                        onClick={handleReset}
                        disabled={submitting || !isModified}
                        className={cx(classes.textButton, classes.reset)}
                    >
                        Reset
                    </Button>
                    <Button onClick={clearState} className={classes.textButton}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.primary}
                        disabled={submitting || !isModified}
                        onClick={handleSubmit}
                    >
                        {submitting ? "Updating…" : "Update"}
                    </Button>
                </>
            }
        >
            <DialogSwitchRow
                label="Active"
                checked={currentTimeFrameDay.active}
                onChange={() => handlePropertyChange('active', !currentTimeFrameDay.active)}
            />
            <DialogSwitchRow
                label="All day"
                hint="From midnight to midnight."
                checked={currentTimeFrameDay.allDay}
                disabled={!currentTimeFrameDay.active}
                onChange={() => handlePropertyChange('allDay', !currentTimeFrameDay.allDay)}
            />
            {renderTime("Start", currentTimeFrameDay.start, handleStartTimeChange)}
            {renderTime("End", currentTimeFrameDay.end, handleEndTimeChange)}
        </AdminDialog>
    );
}

const mapStateToProps = (state) => {
    return {
        user: state.user,
        session: state.session,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TimeFrameDay);